/* global globalThis */
// File: src/main.js

import 'bootstrap'
import 'vue-toastification/dist/index.css'
import './assets/app.scss'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPersist from 'pinia-plugin-persist'
import App from './App.vue'
import router from './router'
import { initializeSentry } from '@/config/sentry'
import { initializeErrorHandler } from '@/utils/ErrorHandler'
import { telemetryService } from '@/services/telemetry.service'
import i18n, { initializeI18n } from './i18n'

import { useMainStore } from '@/stores/main'

/**
 * Mirror of boot-loader.js checkReloadAllowed() using the same sessionStorage
 * key so counts accumulate across both code paths. Prevents perpetual reload
 * loops when boot-loader.js itself fails to load.
 *
 * @returns {boolean} true if a reload is permitted, false if capped.
 */
function checkReloadAllowed() {
    const MAX_RELOADS = 3
    const WINDOW_MS = 60000
    const KEY = '_lbr'
    try {
        const now = Date.now()
        const raw = sessionStorage.getItem(KEY)
        const rec = raw ? JSON.parse(raw) : null
        if (rec && now - rec.t < WINDOW_MS) {
            if (rec.n >= MAX_RELOADS) return false
            sessionStorage.setItem(KEY, JSON.stringify({ n: rec.n + 1, t: rec.t }))
        } else {
            sessionStorage.setItem(KEY, JSON.stringify({ n: 1, t: now }))
        }
        return true
    } catch {
        // sessionStorage unavailable — allow reload
        return true
    }
}

/**
 * Last-resort error UI when boot-loader.js is unavailable.
 * Shows a localised error message and schedules a guarded reload.
 */
function showFallbackError() {
    const spinner = document.getElementById('vite-reload-spinner')
    const text = document.getElementById('loading-text')
    if (spinner) spinner.classList.add('loading-error')
    if (text) {
        let storedLocale = null
        try {
            storedLocale = localStorage.getItem('locale')
        } catch {
            // localStorage unavailable in restricted/private-browsing environments
        }
        const base = (storedLocale || navigator.language || 'en').split('-')[0]
        const msgs = globalThis.__loadingMessages?.[base]
        text.textContent = msgs?.countdown?.(5) ?? 'Something went wrong. Reloading in 5 seconds...'
    }
    if (checkReloadAllowed()) {
        setTimeout(() => location.reload(), 5000)
    }
}

async function bootstrap() {
    let initialized = false
    try {
        const app = createApp(App)
        const pinia = createPinia()
        pinia.use(piniaPersist)
        app.use(pinia)
        app.use(router)

        // Load initial locale dynamically before mounting
        await initializeI18n()

        app.use(i18n)

        // Initialize Sentry/GlitchTip BEFORE error handler
        // This allows Sentry to set up its own error tracking first
        initializeSentry(app)

        // Initialize SigNoz APM (performance monitoring)
        // This runs early to capture app initialization traces
        telemetryService.initialize({
            serviceName: 'lantern-frontend',
            endpoint: import.meta.env.VITE_SIGNOZ_ENDPOINT || '',
            ingestionKey: import.meta.env.VITE_SIGNOZ_INGESTION_KEY || '',
            enabled: import.meta.env.VITE_SIGNOZ_ENABLED === 'true',
            environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
            region: import.meta.env.VITE_SIGNOZ_REGION || 'us'
        })

        // Global error handler (safety net for uncaught errors)
        initializeErrorHandler(app)

        const mainStore = useMainStore()
        console.debug('[main] MainStore initialized:', !!mainStore)

        await router.isReady()
        app.mount('#app')
        initialized = true
    } catch (err) {
        console.error('[bootstrap] Fatal initialization error:', err)
    } finally {
        // Cancel the dead-man's switch timer set by boot-loader.js
        globalThis.__lanternBoot?.clearDmsTimer()
        if (initialized) {
            if (globalThis.__lanternBoot) {
                // Signal successful mount — clears countdown, removes overlay
                globalThis.__lanternBoot.markInitialized()
            } else {
                // boot-loader.js unavailable — remove the loading overlay directly
                document.getElementById('vite-reload-spinner')?.remove()
            }
        } else if (globalThis.__lanternBoot) {
            // Initialization failed — trigger error countdown and auto-reload
            globalThis.__lanternBoot.showError()
        } else {
            showFallbackError()
        }
    }

    // Flush telemetry on page unload
    if (initialized && telemetryService.isEnabled) {
        globalThis.addEventListener('beforeunload', () => {
            telemetryService.flush()
        })
    }
}

bootstrap() // NOSONAR javascript:S7785 — named function intentional for stack trace clarity
